let bridgeSupabaseAccessToken: string | undefined
let bridgeTokenRefresher: (() => Promise<string | undefined>) | undefined
let inflightRefresh: Promise<string | undefined> | undefined

/**
 * Registra quem sabe buscar um token novo no bridge (`/api/session`).
 * Usado para renovar em silencio quando o PostgREST responde 401 (token expirado
 * em abas abertas ha muito tempo), em vez de derrubar a pessoa para o login.
 */
export function setBridgeTokenRefresher(refresher: (() => Promise<string | undefined>) | undefined) {
  bridgeTokenRefresher = refresher
}

async function refreshBridgeToken() {
  if (!bridgeTokenRefresher) return undefined
  // Varias requisicoes podem tomar 401 juntas: uma unica consulta ao bridge.
  inflightRefresh ??= bridgeTokenRefresher().finally(() => {
    inflightRefresh = undefined
  })
  return inflightRefresh
}

/**
 * Pede um token novo ao bridge e guarda. O tempo real usa quando o socket cai
 * por token vencido (aba aberta há horas sem nenhuma chamada REST que renove).
 */
export async function renewBridgeSupabaseAccessToken(): Promise<string | undefined> {
  const refreshed = normalizeToken(await refreshBridgeToken())
  if (refreshed) bridgeSupabaseAccessToken = refreshed
  return refreshed
}

function normalizeToken(token: string | null | undefined) {
  const trimmedToken = token?.trim()
  return trimmedToken ? trimmedToken : undefined
}

export function setBridgeSupabaseAccessToken(token: string | null | undefined) {
  bridgeSupabaseAccessToken = normalizeToken(token)
  return bridgeSupabaseAccessToken
}

export function getBridgeSupabaseAccessToken() {
  return bridgeSupabaseAccessToken
}

export function clearBridgeSupabaseAccessToken() {
  bridgeSupabaseAccessToken = undefined
}

export function shouldApplyGoTrueSession(accessToken: string | null | undefined, refreshToken: string | null | undefined) {
  return Boolean(normalizeToken(accessToken) && normalizeToken(refreshToken))
}

function buildSupabaseHeaders(input: RequestInfo | URL, init?: RequestInit) {
  const headers = new Headers(input instanceof Request ? input.headers : undefined)
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value))
  return headers
}

export function createBridgeSupabaseFetch(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input, init) => {
    const accessToken = getBridgeSupabaseAccessToken()
    const headers = buildSupabaseHeaders(input, init)

    // Se houver bridge token, sobrescreve o Authorization.
    // Senão, mantém o que o Supabase client já injetou (token GoTrue).
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`)
    }

    const response = await baseFetch(input, {
      ...init,
      headers,
    })

    // Corpo ja consumido (stream/FormData de upload) nao pode ser reenviado com seguranca.
    const canRetry = !(input instanceof Request) && !(init?.body instanceof ReadableStream)
    if (response.status !== 401 || !accessToken || !canRetry || !bridgeTokenRefresher) {
      return response
    }

    try {
      const refreshed = normalizeToken(await refreshBridgeToken())
      if (!refreshed || refreshed === accessToken) return response
      setBridgeSupabaseAccessToken(refreshed)
      headers.set("Authorization", `Bearer ${refreshed}`)
      return await baseFetch(input, { ...init, headers })
    } catch {
      return response
    }
  }
}
